import { Component, OnInit, inject } from '@angular/core';
import {
  UntypedFormGroup,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { AdminService } from '../admin.service';
import {
  Application,
  Unit,
  UnitListResponse,
  UploadType,
} from '../../models/admin-models';
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { BuildInformationComponent } from '../build-information/build-information.component';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-uploads',
  templateUrl: './uploads.component.html',
  styleUrls: ['./uploads.component.scss'],
  imports: [
    BuildInformationComponent,
    UtilityToolbarComponent,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatButtonModule,
    MatInputModule,
  ],
})
export class UploadsComponent implements OnInit {
  private admnSrvc = inject(AdminService);

  public fileName: string;
  myForm: FormGroup;
  notePlaceholder = 'Note';
  file: File;
  status = '';
  statusClass = 'neutral';
  filteredUnits;
  unitSuccess = '';
  solutions: Application[] = [
    { value: 'aw', name: 'AW' },
    { value: 'iw', name: 'IW' },
    { value: 'li', name: 'LI' },
    { value: 'pp', name: 'PP' },
    { value: 'se', name: 'SE' },
    { value: 'rb', name: 'RB' },
    { value: 'cm', name: 'CM' },
    { value: 'pm', name: 'PM' },
    { value: 'pmdl', name: 'PMDL' },
    { value: 'dl', name: 'TM' },
    { value: 'arl', name: 'AM' },
    { value: 'fpml', name: 'FPM' },
    { value: 'amlal', name: 'AMLA' },
  ];
  uploadTypes: UploadType[] = [
    { value: 'KBPackage', name: 'KB' },
    { value: 'UIConfigJson', name: 'UI Config' },
    { value: 'unitProfileXml', name: 'Unit Profile' },
    { value: 'user', name: 'User' },
    { value: 'mapping', name: 'Mapping' },
  ];

  constructor() {
    const formGroup = {};
    formGroup['type'] = new FormControl('', Validators.required);
    formGroup['solution'] = new FormControl('');
    formGroup['massUpload'] = new FormControl(false);
    formGroup['note'] = new FormControl('');
    formGroup['unit'] = new FormControl('');
    formGroup['bugTrackerId'] = new FormControl('');
    this.myForm = new UntypedFormGroup(formGroup);
  }

  public listUpload: Array<string> = [
    'KnowledgeBase',
    'AW KnowledgeBase',
    'Rip KnowledgeBase',
    'IW KnowledgeBase',
    'UIConfig',
  ];

  ngOnInit() {
    this.getUnits();
  }

  getUnits() {
    this.filteredUnits = this.myForm.get('unit').valueChanges.pipe(
      startWith('%'),
      distinctUntilChanged(),
      switchMap(value => {
        if (value.length > 2) {
          return this._filterUnits(value);
        }
        return [];
      })
    );
  }

  updateForm() {
    const formType = this.myForm.get('type').value;
    const massUpload = this.myForm.get('massUpload').value;
    if (formType !== 'user') {
      this.myForm.get('solution').setValidators(Validators.required);
      this.myForm.get('solution').updateValueAndValidity();
      this.myForm.get('note').setValidators(Validators.required);
      this.myForm.get('note').updateValueAndValidity();
      if (formType === 'unitProfileXml' && !massUpload) {
        this.myForm.get('unit').setValidators(Validators.required);
        this.myForm.get('unit').updateValueAndValidity();
      } else {
        this.myForm.get('unit').clearValidators();
        this.myForm.get('unit').updateValueAndValidity();
      }
    } else {
      this.myForm.get('solution').clearValidators();
      this.myForm.get('solution').updateValueAndValidity();
      this.myForm.get('note').clearValidators();
      this.myForm.get('note').updateValueAndValidity();
      this.myForm.get('unit').clearValidators();
      this.myForm.get('unit').updateValueAndValidity();
    }
  }

  onFileSelected(event) {
    this.file = event.target.files[0];

    if (this.file) {
      this.fileName = this.file.name;
    }
  }

  onFileSubmit() {
    this.status = null;
    this.statusClass = null;
    let note;
    let solution;
    let unit;

    if (this.myForm.valid && this.file) {
      if (this.myForm.get('type').value !== 'user') {
        note = this.myForm.get('note').value;
        solution = this.myForm.get('solution').value;
        if (
          this.myForm.get('type').value === 'unitProfileXml' &&
          this.myForm.get('massUpload').value === false
        ) {
          unit = this.myForm.get('unit').value;
        }
      }
      const formData = new FormData();
      formData.append(this.myForm.get('type').value, this.file);

      if (this.myForm.get('type').value === 'KBPackage') {
        this.admnSrvc.postKBPackage(formData, note, solution).subscribe({
          next: (res: string | null) => {
            this.handleResponse(res);
          },
          error: error => {
            this.handleResponse(error);
          },
        });
      } else if (this.myForm.get('type').value === 'UIConfigJson') {
        this.admnSrvc.postUIConfig(formData, note, solution).subscribe({
          next: (res: string | null) => {
            this.handleResponse(res);
          },
          error: error => {
            this.handleResponse(error);
          },
        });
      } else if (
        this.myForm.get('type').value === 'unitProfileXml' &&
        this.myForm.get('massUpload').value === false
      ) {
        note = `Bug Tracker #${this.myForm.get('bugTrackerId').value}: ${
          this.myForm.get('note').value
        }`;

        this.admnSrvc
          .postUnitProfile(formData, unit, solution, encodeURIComponent(note))
          .subscribe({
            next: res => {
              this.handleResponse(res);
            },
            error: error => {
              this.handleResponse(error);
            },
          });
      } else if (
        this.myForm.get('type').value === 'unitProfileXml' &&
        this.myForm.get('massUpload').value === true
      ) {
        note = `Bug Tracker #${this.myForm.get('bugTrackerId').value}: ${
          this.myForm.get('note').value
        }`;

        this.admnSrvc
          .postMassUnitProfile(formData, solution, encodeURIComponent(note))
          .subscribe({
            next: res => {
              this.handleResponse(res);
            },
            error: error => {
              this.handleResponse(error);
            },
          });
      } else if (this.myForm.get('type').value === 'user') {
        this.admnSrvc.postUserUpload(formData).subscribe({
          next: res => {
            this.handleResponse(res);
          },
          error: error => {
            this.handleResponse(error);
          },
        });
      } else if (this.myForm.get('type').value === 'mapping') {
        formData.delete(this.myForm.get('type').value);
        formData.append('Mappings', this.file);
        this.admnSrvc.postMappingFile(formData).subscribe({
          next: res => {
            if (res && res['results']) {
              this.status = `Uploaded ${this.fileName}`;
              this.statusClass = 'success';
            } else {
              this.status = `Error uploading ${this.fileName}`;
              this.statusClass = 'failure';
            }
          },
          error: error => {
            this.handleResponse(error);
          },
        });
      }
    } else {
      this.status = 'Missing required input';
      this.statusClass = 'failure';
    }
  }

  handleResponse(response: string | null) {
    if (response) {
      const parsed = JSON.parse(response);
      const responseText =
        parsed['result'] || parsed['results'] || parsed['status'];
      if (responseText.toLowerCase() === 'success') {
        this.unitSuccess =
          this.myForm.get('unit').value || this.myForm.get('solution').value;
        this.status = `Uploaded ${this.fileName}`;
        this.statusClass = 'success';
      } else if (responseText === 'Invalid KB File') {
        this.status = `Invalid KB File`;
        this.statusClass = 'failure';
      } else {
        this.status = `Error uploading ${this.fileName}: ${responseText}`;
        this.statusClass = 'failure';
      }
    }
  }

  private _filterUnits(value: string) {
    if (!value) {
      value = '%25';
    }
    const list = this.admnSrvc
      .getUnitList(value, true)
      .pipe(map((data: UnitListResponse) => data.names));
    return list;
  }
}
